"use client";

import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { createEmirate, updateEmirate, deleteEmirate } from "../../api/emirates";
import customFetch from "../../hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";

const EmiratesTab = () => {
  const [rows, setRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    eid: null,
    name: "",
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Excel menu state
  const [excelMenuAnchorEl, setExcelMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchEmirates();
  }, []);

  const fetchEmirates = async () => {
    try {
      const data = await customFetch("emirates");
      setRows(data);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  // Optimistic update helpers
  const updateLocalRows = (updatedEmirate) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.eid === updatedEmirate.eid ? { ...row, ...updatedEmirate } : row))
    );
  };

  const addLocalRow = (newEmirate) => {
    setRows((prevRows) => [...prevRows, newEmirate]);
  };

  const removeLocalRow = (id) => {
    setRows((prevRows) => prevRows.filter((row) => row.eid !== id));
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setFormData({ eid: null, name: "" });
    setOpenModal(true);
  };

  const handleOpenEditModal = (emirate) => {
    setIsEditMode(true);
    setFormData({
      eid: emirate.eid,
      name: emirate.name || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({ eid: null, name: "" });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert("Emirate Name is required");
      return;
    }

    const payload = { name: formData.name };

    try {
      let response;
      if (isEditMode) {
        payload.eid = formData.eid;
        response = await updateEmirate(formData.eid, payload);
        if (response.status) {
          updateLocalRows({ ...payload, eid: formData.eid });
          alert("Emirate updated successfully!");
          handleCloseModal();
        }
      } else {
        response = await createEmirate(payload);
        if (response.status && response.data && response.data.length > 0) {
          const createdEmirate = response.data[0];
          addLocalRow(createdEmirate);
          alert("Emirate added successfully!");
          handleCloseModal();
        } else {
          // Fallback refetch if no data returned
          await fetchEmirates();
          alert("Emirate added successfully!");
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error("Error saving emirate:", error);
      alert("Failed to save emirate");
    }
  };

  const handleDelete = (id) => () => {
    setDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteEmirate(deleteId);
      removeLocalRow(deleteId);
      alert("Emirate deleted successfully!");
    } catch (error) {
      console.error("Error deleting emirate:", error);
      alert("Failed to delete emirate");
    }
    setOpenConfirmDialog(false);
  };

  // Excel handlers (unchanged)
  const handleExcelButtonClick = (event) => {
    setExcelMenuAnchorEl(event.currentTarget);
  };

  const handleExcelMenuClose = () => {
    setExcelMenuAnchorEl(null);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [["name"]];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Emirates");
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
    const fileName = `emirates_template_${dateStr}_${timeStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
    handleExcelMenuClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
    handleExcelMenuClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const data = await readExcelFile(file);
      if (data && data.length > 0) {
        await bulkCreateEmirates(data);
      }
    } catch (error) {
      console.error("Error processing Excel file:", error);
    }
    event.target.value = null;
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          const validData = jsonData.filter((item) => item.name && typeof item.name === "string");
          resolve(validData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const bulkCreateEmirates = async (emiratesData) => {
    try {
      const creationPromises = emiratesData.map((emirate) => createEmirate({ name: emirate.name }));
      const results = await Promise.all(creationPromises);
      const newEmirates = results
        .filter((response) => response.status && response.data && response.data.length > 0)
        .flatMap((response) => response.data);

      if (newEmirates.length > 0) {
        setRows((prevRows) => [...prevRows, ...newEmirates]);
      }
    } catch (error) {
      console.error("Error creating emirates in bulk:", error);
    }
  };

  const columns = [
    { field: "eid", headerName: "Emirate ID", width: 100 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenEditModal(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDelete(params.id)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExcelButtonClick}
          startIcon={<BackupTableIcon />}
        >
          Excel
        </Button>
        <Menu
          anchorEl={excelMenuAnchorEl}
          open={Boolean(excelMenuAnchorEl)}
          onClose={handleExcelMenuClose}
        >
          <MenuItem onClick={handleDownloadTemplate}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Template</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleUploadClick}>
            <ListItemIcon>
              <FileUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload File</ListItemText>
          </MenuItem>
        </Menu>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: "none" }}
        />
        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Emirate
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.eid}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Modal for Add/Edit */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <h2>{isEditMode ? "Edit Emirate" : "Add New Emirate"}</h2>

          <TextField
            autoFocus
            margin="dense"
            label="Emirate Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this Emirate? This action cannot be undone."
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default EmiratesTab;
